import React, { useState } from 'react';
import { ArrowRight, DollarSign, Check, CreditCard, Wallet, Building, Globe } from 'lucide-react';
const PaymentProcessor = () => {
  const [paymentMethod, setPaymentMethod] = useState('wallet');
  const [amount, setAmount] = useState('');
  const [recipient, setRecipient] = useState('');
  const [note, setNote] = useState('');
  const [paymentType, setPaymentType] = useState('transfer');
  const [currency, setCurrency] = useState('USD');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  // Mock data for demonstration
  const wallets = [{
    id: 1,
    name: 'USD Wallet',
    balance: 2540.5,
    currency: 'USD'
  }, {
    id: 2,
    name: 'GBP Wallet',
    balance: 1250.75,
    currency: 'GBP'
  }, {
    id: 3,
    name: 'NGN Wallet',
    balance: 450000.0,
    currency: 'NGN'
  }];
  const savedRecipients = [{
    id: 1,
    name: 'John Smith',
    email: 'john.smith@example.com',
    accountNumber: '1234567890'
  }, {
    id: 2,
    name: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    accountNumber: '0987654321'
  }, {
    id: 3,
    name: 'Michael Brown',
    email: 'michael.b@example.com',
    accountNumber: '5432167890'
  }];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    // Simulate API call
    setTimeout(() => {
      setIsProcessing(false);
      setIsComplete(true);
      // Reset form after showing success for a bit
      setTimeout(() => {
        setIsComplete(false);
        setAmount('');
        setRecipient('');
        setNote('');
      }, 3000);
    }, 2000);
  };
  const getCurrencySymbol = (currencyCode: string) => {
    const symbols: {
      [key: string]: string;
    } = {
      USD: '$',
      GBP: '£',
      NGN: '₦',
      ZAR: 'R',
      GHS: '₵'
    };
    return symbols[currencyCode] || currencyCode;
  };
  return <div className="py-6" data-id="element-545">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8" data-id="element-546">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-547">
          Payment Processor
        </h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-548">
          Send money, pay bills, and manage your transactions
        </p>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-6" data-id="element-549">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-550">
          {isComplete ? <div className="px-4 py-16 sm:px-6 flex flex-col items-center justify-center" data-id="element-551">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100" data-id="element-552">
                <Check className="h-6 w-6 text-green-600" aria-hidden="true" data-id="element-553" />
              </div>
              <div className="mt-3 text-center sm:mt-5" data-id="element-554">
                <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-555">
                  Payment Successful!
                </h3>
                <div className="mt-2" data-id="element-556">
                  <p className="text-sm text-gray-500" data-id="element-557">
                    Your payment of {getCurrencySymbol(currency)}
                    {amount} has been processed successfully.
                  </p>
                </div>
                <div className="mt-5" data-id="element-558">
                  <button type="button" onClick={() => setIsComplete(false)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-559">
                    Make Another Payment
                  </button>
                </div>
              </div>
            </div> : <form onSubmit={handleSubmit} data-id="element-560">
              <div className="px-4 py-5 sm:p-6" data-id="element-561">
                {/* Payment Type Selector */}
                <div className="mb-6" data-id="element-562">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-563">
                    Payment Type
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-564">
                    <button type="button" className={`py-3 px-4 border rounded-md flex items-center justify-center ${paymentType === 'transfer' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`} onClick={() => setPaymentType('transfer')} data-id="element-565">
                      <ArrowRight className="h-5 w-5 mr-2" data-id="element-566" />
                      <span data-id="element-567">Money Transfer</span>
                    </button>
                    <button type="button" className={`py-3 px-4 border rounded-md flex items-center justify-center ${paymentType === 'bill' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`} onClick={() => setPaymentType('bill')} data-id="element-568">
                      <DollarSign className="h-5 w-5 mr-2" data-id="element-569" />
                      <span data-id="element-570">Bill Payment</span>
                    </button>
                    <button type="button" className={`py-3 px-4 border rounded-md flex items-center justify-center ${paymentType === 'international' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`} onClick={() => setPaymentType('international')} data-id="element-571">
                      <Globe className="h-5 w-5 mr-2" data-id="element-572" />
                      <span data-id="element-573">International</span>
                    </button>
                  </div>
                </div>
                {/* Payment Method Selector */}
                <div className="mb-6" data-id="element-574">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-575">
                    Payment Method
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-576">
                    <button type="button" className={`py-3 px-4 border rounded-md flex items-center justify-center ${paymentMethod === 'wallet' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`} onClick={() => setPaymentMethod('wallet')} data-id="element-577">
                      <Wallet className="h-5 w-5 mr-2" data-id="element-578" />
                      <span data-id="element-579">Wallet</span>
                    </button>
                    <button type="button" className={`py-3 px-4 border rounded-md flex items-center justify-center ${paymentMethod === 'card' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`} onClick={() => setPaymentMethod('card')} data-id="element-580">
                      <CreditCard className="h-5 w-5 mr-2" data-id="element-581" />
                      <span data-id="element-582">Card</span>
                    </button>
                    <button type="button" className={`py-3 px-4 border rounded-md flex items-center justify-center ${paymentMethod === 'bank' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`} onClick={() => setPaymentMethod('bank')} data-id="element-583">
                      <Building className="h-5 w-5 mr-2" data-id="element-584" />
                      <span data-id="element-585">Bank Account</span>
                    </button>
                  </div>
                </div>
                {/* Source Selection */}
                {paymentMethod === 'wallet' && <div className="mb-6" data-id="element-586">
                    <label htmlFor="wallet" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-587">
                      Select Wallet
                    </label>
                    <select id="wallet" name="wallet" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" onChange={e => setCurrency(wallets.find(w => w.id === parseInt(e.target.value))?.currency || 'USD')} data-id="element-588">
                      {wallets.map(wallet => <option key={wallet.id} value={wallet.id} data-id="element-589">
                          {wallet.name} ({getCurrencySymbol(wallet.currency)}
                          {wallet.balance.toLocaleString()})
                        </option>)}
                    </select>
                  </div>}
                {paymentMethod === 'card' && <div className="mb-6" data-id="element-590">
                    <label htmlFor="card" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-591">
                      Select Card
                    </label>
                    <select id="card" name="card" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" data-id="element-592">
                      <option value="visa" data-id="element-593">Visa ending in 4242</option>
                      <option value="mastercard" data-id="element-594">
                        Mastercard ending in 5555
                      </option>
                      <option value="new" data-id="element-595">Add a new card</option>
                    </select>
                  </div>}
                {/* Recipient Selection */}
                <div className="mb-6" data-id="element-596">
                  <label htmlFor="recipient" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-597">
                    {paymentType === 'bill' ? 'Select Service Provider' : 'Recipient'}
                  </label>
                  {paymentType === 'bill' ? <select id="recipient" name="recipient" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" value={recipient} onChange={e => setRecipient(e.target.value)} data-id="element-598">
                      <option value="" data-id="element-599">Select a service provider</option>
                      <option value="electricity" data-id="element-600">Electricity</option>
                      <option value="water" data-id="element-601">Water</option>
                      <option value="internet" data-id="element-602">Internet</option>
                      <option value="cable" data-id="element-603">Cable TV</option>
                      <option value="phone" data-id="element-604">Phone</option>
                    </select> : <div className="space-y-4" data-id="element-605">
                      <select id="saved-recipient" name="saved-recipient" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" value={recipient} onChange={e => setRecipient(e.target.value)} data-id="element-606">
                        <option value="" data-id="element-607">
                          Select a saved recipient or enter new
                        </option>
                        {savedRecipients.map(r => <option key={r.id} value={r.email} data-id="element-608">
                            {r.name} ({r.email})
                          </option>)}
                      </select>
                      {!savedRecipients.some(r => r.email === recipient) && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-609">
                          <div data-id="element-610">
                            <label htmlFor="new-recipient" className="block text-sm font-medium text-gray-700" data-id="element-611">
                              {paymentType === 'international' ? 'SWIFT/BIC Code' : 'Email or Account Number'}
                            </label>
                            <input type="text" name="new-recipient" id="new-recipient" className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={recipient} onChange={e => setRecipient(e.target.value)} placeholder={paymentType === 'international' ? 'Enter SWIFT/BIC code' : 'Enter email or account number'} data-id="element-612" />
                          </div>
                          {paymentType === 'international' && <div data-id="element-613">
                              <label htmlFor="bank-name" className="block text-sm font-medium text-gray-700" data-id="element-614">
                                Bank Name
                              </label>
                              <input type="text" name="bank-name" id="bank-name" className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="Enter bank name" data-id="element-615" />
                            </div>}
                        </div>}
                    </div>}
                </div>
                {/* Amount Input */}
                <div className="mb-6" data-id="element-616">
                  <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-617">
                    Amount
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm" data-id="element-618">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-619">
                      <span className="text-gray-500 sm:text-sm" data-id="element-620">
                        {getCurrencySymbol(currency)}
                      </span>
                    </div>
                    <input type="text" name="amount" id="amount" className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} required data-id="element-621" />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-622">
                      <span className="text-gray-500 sm:text-sm" data-id="element-623">
                        {currency}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Note/Reference */}
                <div className="mb-6" data-id="element-624">
                  <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-625">
                    Note/Reference (Optional)
                  </label>
                  <input type="text" name="note" id="note" className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="Add a note or reference" value={note} onChange={e => setNote(e.target.value)} data-id="element-626" />
                </div>
                {/* Submit Button */}
                <div className="pt-5" data-id="element-627">
                  <div className="flex justify-end" data-id="element-628">
                    <button type="button" className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-629">
                      Cancel
                    </button>
                    <button type="submit" disabled={isProcessing || !amount || !recipient} className={`ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${isProcessing || !amount || !recipient ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`} data-id="element-630">
                      {isProcessing ? <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-631">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-632"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-633"></path>
                          </svg>
                          Processing...
                        </> : 'Process Payment'}
                    </button>
                  </div>
                </div>
              </div>
            </form>}
        </div>
      </div>
    </div>;
};
export default PaymentProcessor;